import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismIcon,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralSmartInsightsBadgePriority from 'lumen/components/general/smart-insights/badge-priority';
import GeneralSmartInsightsDetailViewDropdownArchive from 'lumen/components/general/smart-insights/detail-view/dropdown-archive';
import GeneralSmartInsightsDetailViewSmartInsightCard from 'lumen/components/general/smart-insights/detail-view/smart-insight-card';
import GeneralSmartInsightsDetailViewSmartInsightRecommendationList from 'lumen/components/general/smart-insights/detail-view/smart-insight-recommendation-list';
import type {
  SmartInsightDay,
  SmartInsightDayInsightEntry,
} from 'lumen/models';
import {
  SMART_INSIGHT_DAY_ENTRY_TYPE,
  type SmartInsightDayInsightEntrySource,
} from 'lumen/models/smart-insights/smart-insight-day-insight-entry';
import {
  SMART_INSIGHTS_FEEDBACK_TYPE_TO_USERSNAP_EVENT,
  SMART_INSIGHTS_FEEDBACK_TYPES,
  type SmartInsightsArchiveReason,
  type SmartInsightsFeedbackType,
} from 'lumen/utils/constants/smart-insights';

import styles from './smart-insight-entry-list.module.css';

interface GeneralSmartInsightsDetailViewSmartInsightEntryListSignature {
  Args: {
    isArchiving?: boolean;
    onArchive?: (reason: SmartInsightsArchiveReason) => void;
    openInNewTab: boolean;
    product: 'smart-insights' | 'smart-insights-multi-property';
    showFeedback?: boolean;
    showSource?: boolean;
    smartInsightDay: SmartInsightDay;
  };
}

export default class GeneralSmartInsightsDetailViewSmartInsightEntryList extends Component<GeneralSmartInsightsDetailViewSmartInsightEntryListSignature> {
  @service declare elementHighlighter: Services['element-highlighter'];
  @service declare smartInsights: Services['smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare usersnap: Services['usersnap'];

  FEEDBACK_TYPES = SMART_INSIGHTS_FEEDBACK_TYPES;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedStayDate() {
    const { smartInsightDay } = this.args;

    return dayjs(smartInsightDay.stayDate).format('ddd, MMM D, YYYY');
  }

  get showFeedback(): boolean {
    return this.args.showFeedback !== false;
  }

  get showSource(): boolean {
    return this.args.showSource !== false;
  }

  @action onClickFeedback(type: SmartInsightsFeedbackType): void {
    const { smartInsightDay } = this.args;

    this.usageAnalytics.track('Feedback Given', {
      eventTrigger: 'smart-insights-detail-view',
      product: this.args.product,
      feedbackType: type,
      ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
        smartInsightDay,
        smartInsightDay.stayDate,
      ),
    });

    this.usersnap.logEvent(
      SMART_INSIGHTS_FEEDBACK_TYPE_TO_USERSNAP_EVENT[type],
    );
  }

  @action onClickSource(
    source: SmartInsightDayInsightEntrySource,
    event: MouseEvent,
  ): void {
    event.preventDefault();

    const { navigateToSource } = source;
    const { smartInsightDay, openInNewTab } = this.args;

    // Reset the highlighter so the same element can be highlighted again when clicking a new insight
    this.elementHighlighter.reset();

    navigateToSource(openInNewTab);

    this.usageAnalytics.track('Smart Insight Source Viewed', {
      eventTrigger: 'smart-insights-detail-view',
      product: this.args.product,
      ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
        smartInsightDay,
        smartInsightDay.stayDate,
      ),
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  shouldShowDateRange(smartInsightEntry: SmartInsightDayInsightEntry) {
    const { insightElementType } = smartInsightEntry.info;

    if (insightElementType === SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS) {
      return false;
    }

    const { to, from } = smartInsightEntry.dateRange;

    return !dayjs(to).isSame(dayjs(from));
  }

  <template>
    <div
      class="ic-product-tour-57546189-smart-insights-insights flex flex-col gap-200"
    >
      <div class="flex flex-row items-center justify-between">
        <h4 class="text-400--bold">
          {{t "smart_insights.detail_view.insights"}}
        </h4>

        <GeneralSmartInsightsBadgePriority
          @priority={{@smartInsightDay.stayDatePriority}}
        />
      </div>

      <GeneralSmartInsightsDetailViewSmartInsightCard
        class="{{styles.insight-entry}}
          gap-400 px-500 pt-500
          {{if this.showFeedback 'pb-200' 'pb-500'}}"
      >
        {{#each
          @smartInsightDay.insightEntriesSortedByRelevance
          as |smartInsightEntry|
        }}
          <div class="flex flex-col gap-400">
            <div
              class="flex flex-row gap-400"
              data-test-id="smart-insight-detail-view-entry"
            >
              <div
                class="{{styles.icon-container}}
                  flex items-center justify-center"
              >
                <PrismIcon
                  @color="on-intelligence"
                  @name={{smartInsightEntry.iconName}}
                  @size="small"
                  data-test-id="smart-insight-detail-view-entry-icon"
                />
              </div>

              <div class="flex flex-col gap-100">
                <div class="flex flex-col">
                  <p
                    class="text-300--regular text-neutral-default"
                    data-test-id="smart-insight-detail-view-entry-text"
                  >
                    {{htmlSafe smartInsightEntry.formattedText}}
                  </p>
                </div>

                {{#if (this.shouldShowDateRange smartInsightEntry)}}
                  <div class="flex flex-row items-center gap-100">
                    <div
                      class="text-200--medium text-neutral-default"
                      data-test-id="smart-insight-detail-view-entry-date-range"
                    >
                      {{smartInsightEntry.formattedDateRange}}
                    </div>

                    <GeneralIconWithTooltip
                      @tooltipContent={{t
                        "smart_insights.detail_view.source.connected_days.tooltip"
                      }}
                    />
                  </div>
                {{/if}}

                {{#if this.showSource}}
                  {{#if smartInsightEntry.source}}
                    <span
                      class="text-200--regular text-neutral-subdued flex flex-row gap-100"
                    >
                      {{t "smart_insights.detail_view.source"}}

                      <a
                        class="{{styles.source-link}}
                          prism-link text-neutral-subdued"
                        data-test-id="smart-insight-detail-view-entry-source"
                        href={{smartInsightEntry.source.href}}
                        {{on
                          "click"
                          (fn this.onClickSource smartInsightEntry.source)
                        }}
                      >
                        {{smartInsightEntry.source.productName}}
                      </a>
                    </span>
                  {{/if}}
                {{/if}}
              </div>
            </div>
          </div>
        {{/each}}

        {{#if @smartInsightDay.visibleRecommendationEntries.length}}
          <GeneralSmartInsightsDetailViewSmartInsightRecommendationList
            @entries={{@smartInsightDay.visibleRecommendationEntries}}
          />
        {{/if}}

        {{#if this.showFeedback}}
          <div class="flex flex-col gap-300">
            <div class="divider-line mt-100 mb-0" />

            <div class="flex flex-row items-center justify-between">
              <div class="flex flex-row items-center">
                <p class="text-200--regular text-neutral-default">
                  {{t "smart_insights.detail_view.footer.feedback"}}
                </p>

                <div class="ml-100 flex flex-row">
                  <PrismButton
                    @buttonType="ghost"
                    @icon="thumbs-up"
                    @onClick={{fn
                      this.onClickFeedback
                      this.FEEDBACK_TYPES.POSITIVE
                    }}
                    @size="small"
                    data-test-id="feedback-button-positive"
                  >
                    <PrismTooltip
                      @side="bottom"
                      @text={{t
                        "smart_insights.detail_view.footer.feedback_useful"
                      }}
                    />
                  </PrismButton>

                  <PrismButton
                    @buttonType="ghost"
                    @icon="thumbs-down"
                    @onClick={{fn
                      this.onClickFeedback
                      this.FEEDBACK_TYPES.NEGATIVE
                    }}
                    @size="small"
                    data-test-id="feedback-button-negative"
                  >
                    <PrismTooltip
                      @side="bottom"
                      @text={{t
                        "smart_insights.detail_view.footer.feedback_not_useful"
                      }}
                    />
                  </PrismButton>
                </div>
              </div>

              {{#if @onArchive}}
                <GeneralSmartInsightsDetailViewDropdownArchive
                  @isArchiving={{@isArchiving}}
                  @onArchive={{@onArchive}}
                />
              {{/if}}
            </div>
          </div>
        {{/if}}
      </GeneralSmartInsightsDetailViewSmartInsightCard>
    </div>
  </template>
}
